import { useMemo } from "react";

import { useTiVoDeviceData } from "components/form/fields/TiVoTargets";
import { TiVoTargets } from "hooks/tivoApps";

import { TEXTS } from "./constants";
import styles from "./TiVoTargetsTable.scss";

function checkList(list: string[], values?: string[]) {
  const found = [],
    remaining = [];
  for (const id of list) {
    if (values?.includes(id)) {
      found.push(id);
    } else {
      remaining.push(id);
    }
  }
  return [found, remaining];
}

export type TiVoTargetsTableProps = {
  targets: TiVoTargets;
  restrictTargets?: TiVoTargets;
};

export function TiVoTargetsTable({
  targets,
  restrictTargets,
}: TiVoTargetsTableProps) {
  const { getClientTypeIds, getDeviceLabel, getOems } = useTiVoDeviceData();

  const oems = useMemo(
    () => getOems(targets.platforms, restrictTargets),
    [getOems, targets.platforms, restrictTargets]
  );

  function renderClientsBulk(oemIds: string[]) {
    const clientIds = [],
      remainingIds = [];
    for (const oemId of oemIds) {
      const clientTypeIds = getClientTypeIds(
        targets.platforms,
        [oemId],
        restrictTargets
      );
      const [selectedIds] = checkList(clientTypeIds, targets["clientTypes"]);
      if (selectedIds.length === 0) {
        remainingIds.push(oemId);
      } else {
        clientIds.unshift(...selectedIds);
      }
    }
    return clientIds.length === 0 ? (
      <ul>
        <li>
          <p>{TEXTS.ALL_CLIENTS}</p>
        </li>
      </ul>
    ) : (
      <ul>
        {clientIds.map((id) => (
          <li>
            <p>{getDeviceLabel(id)}</p>
          </li>
        ))}
        {remainingIds.length > 0 ? (
          <li>
            <p>
              {TEXTS.ALL_FOR}{" "}
              {remainingIds.map((id) => getDeviceLabel(id)).join(", ")}
            </p>
          </li>
        ) : null}
      </ul>
    );
  }

  function renderClients(oemId: string) {
    const clientTypeIds = getClientTypeIds(
      targets.platforms,
      [oemId],
      restrictTargets
    );
    const [clientIds] = checkList(clientTypeIds, targets["clientTypes"]);
    return (
      <ul>
        {clientIds.length === 0 ? (
          <li>
            <p>{TEXTS.ALL_CLIENTS}</p>
          </li>
        ) : (
          clientIds.map((clientId) => (
            <li>
              <p>{getDeviceLabel(clientId)}</p>
            </li>
          ))
        )}
      </ul>
    );
  }

  function renderOems(platformId: string) {
    const [oemIds, remainingIds] = checkList(oems[platformId], targets["oems"]);

    return oemIds.length === 0 ? (
      <ul>
        <li>
          <p>{TEXTS.ALL_OEMS}</p>
          {renderClientsBulk(remainingIds)}
        </li>
      </ul>
    ) : (
      <ul>
        {oemIds.map((oemId) => (
          <li key={oemId}>
            <p>{getDeviceLabel(oemId)}</p>
            {renderClients(oemId)}
          </li>
        ))}
      </ul>
    );
  }

  return (
    <>
      <ul className={styles.header}>
        <li>{TEXTS.HEADER_PLATFORM}</li>
        <li>{TEXTS.HEADER_OEM}</li>
        <li>{TEXTS.HEADER_CLIENT}</li>
      </ul>
      {Object.values(targets).flat().length === 0 ? (
        <p className={styles.noValue}>{TEXTS.NO_VALUE}</p>
      ) : (
        <ul className={styles.targets}>
          {Object.keys(oems).map((platformId) => (
            <li key={platformId}>
              <p>{getDeviceLabel(platformId)}</p>
              {renderOems(platformId)}
            </li>
          ))}
        </ul>
      )}
    </>
  );
}
